import * as React from 'react'
import * as SelectPrimitive from '@radix-ui/react-select'
import { Check, ChevronDown, ChevronUp } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Figma (node 60:435 — Combobox / Select):
 * Trigger: h-8, rounded-lg, border border-input, bg-background, pl-2.5 pr-1 py-1, gap-1.5
 *   Text: text-sm, placeholder=text-muted-foreground, selected=text-foreground
 *   ChevronDown: 12px in 24px ghost area
 *   Invalid: border-destructive
 *   Focus: ring-2 ring-ring ring-offset-2
 *   Disabled: opacity-50, pointer-events-none
 * Content: bg-popover, border border-border, rounded-lg, shadow-md, p-1
 * Item: rounded-md, pl-1.5 pr-8 py-1, gap-2, text-sm text-popover-foreground
 *   Hover: bg-accent text-accent-foreground
 *   Selected: Check icon (16px) absolute right-2
 * Label: px-1.5 py-1, text-sm font-medium text-muted-foreground
 * Separator: h-px bg-border, -mx-1 my-1
 */

// --- Select (Root) ---

const IntuiSelect = SelectPrimitive.Root

// --- SelectGroup ---

const IntuiSelectGroup = SelectPrimitive.Group

// --- SelectValue ---

const IntuiSelectValue = SelectPrimitive.Value

// --- SelectTrigger ---

export interface IntuiSelectTriggerProps
  extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> {
  invalid?: boolean
}

const IntuiSelectTrigger = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Trigger>,
  IntuiSelectTriggerProps
>(({ className, invalid, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    aria-invalid={invalid || undefined}
    className={cn(
      // Dark-mode pages are lighter than `background`, so a trigger painting
      // its own would sit darker than the surface and read as already clicked.
      'flex h-8 w-full items-center gap-1.5 rounded-lg border bg-background dark:bg-transparent pl-2.5 pr-1 py-1 text-sm',
      'ring-offset-background [&>span]:line-clamp-1 [&>span]:text-left',
      'placeholder:text-muted-foreground',
      'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
      'disabled:cursor-not-allowed disabled:opacity-50',
      invalid ? 'border-destructive' : 'border-input',
      className
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="ml-auto h-3 w-3 shrink-0 opacity-50" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
))
IntuiSelectTrigger.displayName = 'IntuiSelectTrigger'

// --- SelectScrollUpButton ---

const IntuiSelectScrollUpButton = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    {...props}
  >
    <ChevronUp className="h-3 w-3" />
  </SelectPrimitive.ScrollUpButton>
))
IntuiSelectScrollUpButton.displayName = 'IntuiSelectScrollUpButton'

// --- SelectScrollDownButton ---

const IntuiSelectScrollDownButton = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    {...props}
  >
    <ChevronDown className="h-3 w-3" />
  </SelectPrimitive.ScrollDownButton>
))
IntuiSelectScrollDownButton.displayName = 'IntuiSelectScrollDownButton'

// --- SelectContent ---

const IntuiSelectContent = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        'relative z-[999999] max-h-96 min-w-[8rem] overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-md',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
        'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        position === 'popper' &&
          'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
        className
      )}
      position={position}
      {...props}
    >
      <IntuiSelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          'p-1',
          position === 'popper' &&
            'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]'
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <IntuiSelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
))
IntuiSelectContent.displayName = 'IntuiSelectContent'

// --- SelectItem ---

const IntuiSelectItem = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex w-full cursor-default select-none items-center gap-2 rounded-md py-1 pl-1.5 pr-8 text-sm outline-none',
      'focus:bg-accent focus:text-accent-foreground',
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className
    )}
    {...props}
  >
    <span className="absolute right-2 flex size-4 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="size-4" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
))
IntuiSelectItem.displayName = 'IntuiSelectItem'

// --- SelectLabel ---

const IntuiSelectLabel = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn('px-1.5 py-1 text-sm font-medium text-muted-foreground', className)}
    {...props}
  />
))
IntuiSelectLabel.displayName = 'IntuiSelectLabel'

// --- SelectSeparator ---

const IntuiSelectSeparator = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    data-slot="separator"
    className={cn('-mx-1 my-1 h-px bg-border', className)}
    {...props}
  />
))
IntuiSelectSeparator.displayName = 'IntuiSelectSeparator'

export {
  IntuiSelect,
  IntuiSelectGroup,
  IntuiSelectValue,
  IntuiSelectTrigger,
  IntuiSelectContent,
  IntuiSelectItem,
  IntuiSelectLabel,
  IntuiSelectSeparator,
}
